import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Lock, LogOut } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [key, setKey] = useState(sessionStorage.getItem("os_admin_key") || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/waitlist`, { headers: { "X-Admin-Key": k } });
      setEntries(res.data.entries);
      setAuthed(true);
      setKey(k);
      sessionStorage.setItem("os_admin_key", k);
    } catch {
      setError("That passkey isn't right.");
      setAuthed(false);
      sessionStorage.removeItem("os_admin_key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async () => {
    const res = await axios.get(`${API}/admin/waitlist.csv`, {
      headers: { "X-Admin-Key": key },
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "oneskin-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem("os_admin_key");
    setAuthed(false);
    setEntries([]);
    setKey("");
    setInput("");
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F5EFE6] px-6 md:px-16 py-10">
      <header className="flex items-center justify-between max-w-5xl mx-auto mb-14">
        <Link to="/" data-testid="admin-home-link">
          <img src="/assets/oneskin-logo.png" alt="One Skin" className="w-24" />
        </Link>
        <Link
          to="/"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#3D6B55] hover:text-[#1E3A2F] transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
      </header>

      {!authed ? (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-md mx-auto mt-16 bg-[#FAF7F2] border border-[#C8DDD1] p-10 shadow-[0_30px_60px_-30px_rgba(30,58,47,0.35)]"
          data-testid="admin-gate"
        >
          <div className="w-12 h-12 rounded-full bg-[#1E3A2F] flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-[#F5EFE6]" />
          </div>
          <h1 className="font-heading font-bold text-[#1E3A2F] text-3xl mb-2">The back room.</h1>
          <p className="text-[#3D6B55] text-sm font-medium mb-8">
            Enter your passkey to view everyone on the list.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(input.trim());
            }}
            data-testid="admin-login-form"
          >
            <input
              type="password"
              data-testid="admin-key-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Passkey"
              className="bg-transparent border-b-2 border-[#1E3A2F]/20 focus:border-[#1E3A2F] text-[#1E3A2F] py-3 outline-none transition-colors duration-300 w-full placeholder:text-[#3D6B55]/40 font-medium mb-6"
            />
            {error && (
              <p data-testid="admin-error" className="text-[#8B1A2A] text-sm font-medium mb-5">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full bg-[#1E3A2F] text-[#F5EFE6] px-8 py-4 rounded-full font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#3D6B55] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Checking…" : "View waitlist"}
            </button>
          </form>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-5xl mx-auto"
          data-testid="admin-dashboard"
        >
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#3D6B55] mb-2">Waitlist</p>
              <h1 className="font-heading font-black text-[#1E3A2F] text-4xl md:text-5xl">
                {entries.length} {entries.length === 1 ? "person" : "people"} in line.
              </h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={download}
                data-testid="admin-download-btn"
                className="inline-flex items-center gap-2 bg-[#8B1A2A] text-[#F5EFE6] px-6 py-3 rounded-full font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#B5404A] transition-colors duration-300"
              >
                <Download className="w-4 h-4" /> Download spreadsheet
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout-btn"
                className="inline-flex items-center gap-2 border border-[#3D6B55]/30 text-[#3D6B55] px-6 py-3 rounded-full font-bold uppercase tracking-[0.12em] text-xs hover:border-[#1E3A2F] hover:text-[#1E3A2F] transition-colors duration-300"
              >
                <LogOut className="w-4 h-4" /> Lock
              </button>
            </div>
          </div>

          <div className="bg-[#FAF7F2] border border-[#C8DDD1] shadow-[0_30px_60px_-30px_rgba(30,58,47,0.25)]">
            <div className="grid grid-cols-12 gap-4 px-6 md:px-8 py-4 border-b border-[#C8DDD1] text-[10px] tracking-[0.25em] uppercase text-[#3D6B55] font-bold">
              <span className="col-span-1">#</span>
              <span className="col-span-5">Email</span>
              <span className="col-span-3">Phone</span>
              <span className="col-span-3 text-right">Joined</span>
            </div>
            {entries.map((entry) => (
              <div
                key={entry.email}
                data-testid={`admin-row-${entry.position}`}
                className="grid grid-cols-12 gap-4 px-6 md:px-8 py-4 border-b border-[#C8DDD1]/60 last:border-b-0 text-sm"
              >
                <span className="col-span-1 font-heading font-bold text-[#B5404A]">{entry.position}</span>
                <span className="col-span-5 text-[#1E3A2F] font-medium truncate">{entry.email}</span>
                <span className="col-span-3 text-[#3D6B55] font-medium">{entry.phone}</span>
                <span className="col-span-3 text-right text-[#3D6B55]/70 text-xs self-center">
                  {entry.created_at ? new Date(entry.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Admin;
